import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { renderAugmentedArtistsNames } from '@utils/artists';
import { formatDate } from '@utils/datetime';
import { framedByDefault } from '@utils/frames';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { getProductBenefits, shopifyToStoryblokDate } from '@utils/product';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { PRODUCT_TAG_NO_COA } from '../../../constants/product';
import { DrawByProductId } from '../../../features/draws/types';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useDrawStage } from '../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../hooks/useIsPreOrder';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useProductPricing } from '../../../hooks/useProductPricing';
import { ProductGateway } from '../../../types/AvantArteProduct';
import {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
  PurchasingState,
} from '../../../types/Purchasing';
import { Header as DialogHeader } from '../../Containers/Dialog/Step/Header';
import { ContentBlockVideo } from '../../ContentBlockVideo';
import { Modal } from '../../Dialog/Modal';
import { Image } from '../../Image';
import { ProductRelatedDigitalProducts } from '../../ProductRelatedDigitalProducts/ProductRelatedDigitalProducts';
import ScrollAnchor from '../../ScrollAnchor';
import { ProductAccordion } from './ProductAccordion';
import { ProductCounterpartsDrawer } from './ProductCounterpartsDrawer';
import { ProductCTA } from './ProductCTA';
import ProductFeesDrawer from './ProductFeesDrawer';
import ProductFramingDrawer from './ProductFramingDrawer';
import { ProductHero } from './ProductHero';
import { ProductReleaseInfo } from './ProductReleaseInfo';
import ProductVariantOptions from './ProductVariantOptions';

export const productVariantsList = [
  'framing',
  'compositions',
  'colourways',
  'finishing',
  'sizes',
  'dimensions',
] as const;

type ProductAbstractV2Props = {
  className?: string;
  blok: ProductV2Storyblok;
  ctaOpenCheckoutDrawerRef: RefObject<{
    openCheckoutDrawer: null | (() => void);
  }>;
  ctaScrollAnchorRef: RefObject<HTMLAnchorElement>;
  purchasingController: ProductPurchasingController<ProductPurchaseDetails>;
  drawController?: DrawByProductId;
  productsGateway: ProductGateway;
  galleryRef?: RefObject<HTMLDivElement>;
  analytics: Analytics;
  draw?: DrawByProductId;
  onViewAsSetHandler?: () => void;
  productState?: PurchasingState;
};

export const ProductAbstractV2 = ({
  analytics,
  blok,
  className,
  ctaOpenCheckoutDrawerRef,
  ctaScrollAnchorRef,
  draw,
  drawController,
  galleryRef,
  onViewAsSetHandler,
  productState,
  productsGateway,
  purchasingController,
}: ProductAbstractV2Props) => {
  const isMobile = useMediaQuery('sm');
  const delayedTransactionalDialogTimer =
    useRef<ReturnType<typeof setTimeout>>();

  const { t } = useTranslation(['product']);
  const [emitter] = useAnalyticsEmitter();

  const [countryCode] = useCountryCode();
  const benefits = getProductBenefits(
    blok,
    purchasingController,
    countryCode,
    draw,
  );

  const productArtists = (
    blok as unknown as ProductStoryblok
  ).productArtist.reduce<{ name: string; slug: string }[]>(
    (acc, { artist }) => {
      if (!artist || typeof artist === 'string') {
        return acc;
      }
      const { name, slug } = artist;
      acc.push({ name, slug });

      return acc;
    },
    [],
  );
  const productPrice = useProductPricing({
    purchasing: purchasingController,
  });

  const productDetails = blok?.details?.[0] ?? null;

  const productLaunchDate = formatDate(
    shopifyToStoryblokDate(purchasingController.product?.launchDate),
  );

  const { relatedDigitalProducts } = blok;
  const highlightVideo = blok.highlightVideo?.map((item) => {
    return { ...item, analytics };
  });

  const drawStage = useDrawStage(drawController);
  const isPreOrder = useIsPreOrder(drawController);

  const [
    relatedDigitalProductsDrawerVisible,
    setRelatedDigitalProductsDrawerVisible,
  ] = useState(false);
  const [feesDrawerVisible, setTaxDetailsDrawerVisible] = useState(false);
  const [framingDrawerVisible, setFramingDrawerVisible] = useState(false);
  const [transactionalDialogVisible, setTransactionalDialogVisible] =
    useState<boolean>();

  const onShowTaxDetails = () => setTaxDetailsDrawerVisible(true);
  const onShowFramingDetails = () => setFramingDrawerVisible(true);
  const blockChainAuction = blok.nftAuction?.[0];
  const { releaseType } = blok as unknown as ProductType;

  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (transactionalDialogVisible !== undefined) {
      if (delayedTransactionalDialogTimer.current)
        clearTimeout(delayedTransactionalDialogTimer.current);
      return;
    }

    const transactionalDialogParam =
      searchParams.get('show') === 'transactionalDialog';
    const isTransactionalDialogEnabled = isMobile && transactionalDialogParam;

    if (!isTransactionalDialogEnabled) {
      return;
    }

    const delayParam = Number(searchParams.get('delay')); // in seconds
    if (delayParam) {
      delayedTransactionalDialogTimer.current = setTimeout(() => {
        if (!transactionalDialogVisible) setTransactionalDialogVisible(true);
      }, delayParam * 1000);
      return;
    }

    setTransactionalDialogVisible(true);
  }, [isMobile, searchParams, transactionalDialogVisible]);

  const transactionalDialogCloseHandler = useCallback(() => {
    if (transactionalDialogVisible) setTransactionalDialogVisible(false);
    const params = new URLSearchParams(searchParams.toString());

    if (params.has('show')) params.delete('show');
    if (params.has('delay')) params.delete('delay');

    if (params.size === new URLSearchParams(searchParams.toString()).size)
      return;

    if (params.size > 0) {
      router.push(`${pathname}?${params}`, { scroll: false });
    } else {
      router.push(`${pathname}`, { scroll: false });
    }
  }, [pathname, router, searchParams, transactionalDialogVisible]);

  return (
    <div className={classNames('w-full', className)}>
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-9 pt-6">
        <ProductHero
          artists={productArtists}
          auction={blockChainAuction}
          price={productPrice}
          productState={productState}
          releaseType={releaseType}
          title={purchasingController.product?.name ?? ' '}
        />

        <ProductVariantOptions
          product={blok}
          productsGateway={productsGateway}
          purchasingController={purchasingController}
        />

        <ProductReleaseInfo
          available={purchasingController?.product?.available}
          closingDate={(blok as unknown as ProductType).closingDate}
          dispatchDate={purchasingController.dispatchDate}
          editionFormat={blok.editionFormat}
          editionNote={(blok as unknown as ProductType).editionNote}
          editionSize={
            blok.editionFormat === 'series'
              ? blok.relatedSeries?.[0]?.series?.content.series[0].products
                  .length ?? undefined
              : purchasingController.product?.numberOfEditions
          }
          framingOffered={purchasingController.product?.framingOffered}
          freeShipping={purchasingController.product?.freeShipping}
          medium={blok.medium}
          onEditionSizeAnchorClick={() => {
            emitter('', 'collection clicked');
            galleryRef?.current?.scrollIntoView({
              behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
            });
          }}
          releaseCollectionGallery={blok.releaseCollectionGallery}
          releaseNote={blok.releaseNote}
          releaseType={(blok as unknown as ProductType).releaseType}
          tags={purchasingController.product?.tags}
          unlocked={purchasingController.unlocked}
        />

        {highlightVideo && highlightVideo?.length > 0 && (
          <div className="md:hidden">
            <ContentBlockVideo blok={highlightVideo[0]} />
          </div>
        )}

        <div className="flex flex-col gap-3">
          {relatedDigitalProducts && relatedDigitalProducts?.length > 0 ? (
            <ProductRelatedDigitalProducts
              image={
                Array.isArray(relatedDigitalProducts[0]?.media)
                  ? relatedDigitalProducts[0].media[0].component === 'image'
                    ? relatedDigitalProducts[0]?.media[0]?.image
                    : relatedDigitalProducts[0]?.media[0]?.coverImage
                  : relatedDigitalProducts[0]?.media
              }
              label={t('digitalProducts.tile.description')}
              onClick={() => {
                setRelatedDigitalProductsDrawerVisible(true);
                emitter('', 'paired NFT clicked');
              }}
            />
          ) : null}
          {onViewAsSetHandler ? (
            <button
              className="link group flex w-full items-center gap-x-3 overflow-hidden rounded-sm border border-divider/20 p-3 text-left"
              onClick={() => {
                emitter('', 'view set clicked', {
                  location: 'product abstract',
                });
                onViewAsSetHandler();
              }}
              type="button"
            >
              {purchasingController.collectionSetImage ? (
                <span className="w-12 shrink-0">
                  <Image
                    alt="Series thumbnail"
                    hoverEffect
                    shape="rounded"
                    src={purchasingController.collectionSetImage}
                    width={48}
                  />
                </span>
              ) : null}
              <div className="flex flex-col">
                <span className="font-bold">
                  {t('collectionSet.seriesOf')}{' '}
                  {purchasingController.collectionSet?.length}
                </span>
                <span>{t('collectionSet.seriesOfDescription')}</span>
              </div>
            </button>
          ) : null}
          <div>
            <ScrollAnchor
              ref={ctaScrollAnchorRef}
              id="transactionBlock"
              offset={-72}
            />

            {transactionalDialogVisible ? null : (
              <div onClick={transactionalDialogCloseHandler}>
                <ProductCTA
                  ref={ctaOpenCheckoutDrawerRef}
                  draw={drawController}
                  onShowFramingDetails={onShowFramingDetails}
                  onShowTaxDetails={onShowTaxDetails}
                  product={blok as unknown as ProductType}
                  productsGateway={productsGateway}
                  purchasing={purchasingController}
                />
              </div>
            )}
          </div>
        </div>

        <ProductAccordion
          auction={blockChainAuction}
          authentication={productDetails.authentication}
          benefits={benefits}
          customDimensions={productDetails.customDimensions}
          description={productDetails.description}
          dimensions={productDetails?.dimensions?.[0]}
          drawStage={drawStage}
          editionSize={purchasingController.product?.numberOfEditions}
          eligibility={productDetails.eligibility}
          finishing={productDetails.finishing}
          framingOptions={productDetails.framingOptions}
          isFrameIncluded={framedByDefault(
            purchasingController.product?.framingOffered,
          )}
          isPreOrder={isPreOrder}
          isShippingRequired={purchasingController.product?.shippingRequired}
          launchDate={productLaunchDate || undefined}
          medium={productDetails.medium}
          noCoa={
            purchasingController.product?.tags?.includes(PRODUCT_TAG_NO_COA) ??
            false
          }
          noTaxCharged={purchasingController.product?.noTaxCharged}
          onFramingDetailsClick={() => onShowFramingDetails()}
          onTaxDetailsClick={() => onShowTaxDetails()}
          packagingDetails={blok?.packaging?.[0]}
          productId={blok.shopifyId}
          releaseType={purchasingController.releaseType}
          tags={purchasingController.product?.tags}
        />
      </div>

      {relatedDigitalProducts && relatedDigitalProducts?.length > 0 ? (
        <ProductCounterpartsDrawer
          counterparts={relatedDigitalProducts}
          isVisible={relatedDigitalProductsDrawerVisible}
          setIsVisible={setRelatedDigitalProductsDrawerVisible}
        />
      ) : null}

      <ProductFeesDrawer
        onClose={() => setTaxDetailsDrawerVisible(false)}
        onShowFramingDetails={onShowFramingDetails}
        onShowTaxDetails={onShowTaxDetails}
        product={blok as unknown as ProductType}
        productsGateway={productsGateway}
        purchasing={purchasingController}
        shown={feesDrawerVisible}
      />

      <ProductFramingDrawer
        onClose={() => setFramingDrawerVisible(false)}
        product={blok as unknown as ProductType}
        purchasingController={purchasingController}
        shown={framingDrawerVisible}
      />

      {transactionalDialogVisible ? (
        <Modal
          open
          title={t('transactionalBlock.dialog.title', {
            artists: renderAugmentedArtistsNames(productArtists),
          })}
        >
          <div className="theme-light w-full overflow-hidden rounded-t-lg bg-white">
            <DialogHeader
              hidden={false}
              id="trans"
              onClose={transactionalDialogCloseHandler}
              title={t('transactionalBlock.dialog.title', {
                artists: renderAugmentedArtistsNames(productArtists),
              })}
            />
            <div className="w-full bg-white px-5 pb-6">
              <ProductCTA
                draw={drawController}
                onShowFramingDetails={onShowFramingDetails}
                onShowTaxDetails={onShowTaxDetails}
                product={blok as unknown as ProductType}
                productsGateway={productsGateway}
                purchasing={purchasingController}
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
};
